import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router-dom";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { Button } from "./ui/button";
import { 
  LayoutDashboard, 
  Building2, 
  Target, 
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  BookOpen,
  Users,
  CreditCard,
  Zap,
  Building,
  Shield,
  Sparkles
} from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { TrialCountdown } from "./TrialCountdown";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { useCompany } from "@/hooks/useCompany";
import { useSubscription } from "@/hooks/useSubscription";

interface NavbarProps {
  user?: {
    name: string;
    email: string;
    role: string;
    avatar?: string;
    createdAt?: Date;
  };
  onLogout?: () => void;
}

const navItems = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Teams', href: '/teams', icon: Building2 },
  { label: 'Sprints', href: '/sprints', icon: Zap },
  { label: 'KPIs', href: '/kpis', icon: Target },
  { label: 'Users', href: '/users', icon: Users },
];

export function Navbar({ user, onLogout }: NavbarProps) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isSuperAdmin } = useSuperAdmin();
  const { company } = useCompany();
  const { isSubscribed } = useSubscription();

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2 shrink-0">
            <img src="/logo-optimized.webp" alt="" width={150} height={100} className="h-8 sm:h-10 w-auto" />
            <span className="font-bold text-lg sm:text-xl text-foreground hidden sm:inline">Outsprinter</span>
          </Link>

          {/* Desktop Navigation - centered */}
          <div className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {navItems.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link key={item.href} to={item.href}>
                  <Button
                    variant={isActive ? "secondary" : "ghost"}
                    size="sm"
                    className={cn(
                      "gap-2 whitespace-nowrap",
                      isActive && "bg-primary/10 text-primary"
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Button>
                </Link>
              );
            })}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-1 sm:gap-2 lg:gap-3 shrink-0">
            {/* Language Switcher */}
            <LanguageSwitcher />
            {/* Trial Countdown - shown when user is logged in */}
            {user && (
              <div className="hidden xl:block">
                <TrialCountdown trialEndsAt={company?.trial_ends_at} companyCreatedAt={company?.created_at} isSubscribed={isSubscribed} />
              </div>
            )}
            {/* User menu */}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 hidden sm:flex">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="hidden lg:block text-left">
                      <p className="text-sm font-medium text-foreground">{user.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <Link to="/profile">
                    <DropdownMenuItem>
                      <Settings className="mr-2 h-4 w-4" />
                      Profile Settings
                    </DropdownMenuItem>
                  </Link>
                  <Link to="/company-settings">
                    <DropdownMenuItem>
                      <Building className="mr-2 h-4 w-4" />
                      Company Settings
                    </DropdownMenuItem>
                  </Link>
                  <Link to="/billing">
                    <DropdownMenuItem>
                      <CreditCard className="mr-2 h-4 w-4" />
                      Subscription
                    </DropdownMenuItem>
                  </Link>
                  {isSuperAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <Link to="/superadmin">
                        <DropdownMenuItem className="text-primary font-medium">
                          <Shield className="mr-2 h-4 w-4" />
                          SuperAdmin Panel
                        </DropdownMenuItem>
                      </Link>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onLogout} className="text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Mobile/Tablet menu toggle */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden h-9 w-9"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile/Tablet Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border bg-card">
            {/* Mobile Trial Countdown */}
            {user && (
              <div className="mb-4 pb-4 border-b border-border">
                <TrialCountdown trialEndsAt={company?.trial_ends_at} companyCreatedAt={company?.created_at} isSubscribed={isSubscribed} />
              </div>
            )}
            
            <div className="flex flex-col gap-1">
              {navItems.map(item => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link 
                    key={item.href} 
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Button
                      variant={isActive ? "secondary" : "ghost"}
                      className={cn(
                        "w-full justify-start gap-3 h-11",
                        isActive && "bg-primary/10 text-primary"
                      )}
                    >
                      <Icon className="w-5 h-5" />
                      {item.label}
                    </Button>
                  </Link>
                );
              })}

              {/* Mobile profile links */}
              {user && (
                <>
                  <div className="border-t border-border my-2" />
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-foreground">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <Link to="/profile" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start gap-3 h-11">
                      <Settings className="w-5 h-5" />
                      Profile Settings
                    </Button>
                  </Link>
                  <Link to="/company-settings" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start gap-3 h-11">
                      <Building className="w-5 h-5" />
                      Company Settings
                    </Button>
                  </Link>
                  <Link to="/billing" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start gap-3 h-11">
                      <CreditCard className="w-5 h-5" />
                      Subscription
                    </Button>
                  </Link>
                  {isSuperAdmin && (
                    <Link to="/superadmin" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start gap-3 h-11 text-primary font-medium">
                        <Shield className="w-5 h-5" />
                        SuperAdmin Panel
                      </Button>
                    </Link>
                  )}
                  <Button 
                    variant="ghost" 
                    className="w-full justify-start gap-3 h-11 text-destructive"
                    onClick={() => { onLogout?.(); setIsMobileMenuOpen(false); }}
                  >
                    <LogOut className="w-5 h-5" />
                    Log out
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
